/**
 * Sanitizes error messages before displaying to users.
 *
 * SDK and RPC errors may contain sensitive information like API keys
 * embedded in RPC URLs, internal contract addresses, gas estimation
 * details, nonce values, and stack traces.
 */

/** Patterns that indicate sensitive content in error messages */
const SENSITIVE_PATTERNS = [
  /https?:\/\/[^\s]+alchemy[^\s]*/i, // Alchemy URLs with API keys
  /https?:\/\/[^\s]+infura[^\s]*/i, // Infura URLs with API keys
  /api[_-]?key[=:]\s*\S+/i, // Generic API key patterns
  /0x[a-f0-9]{64}/i, // Full 32-byte hex values (private data)
];

/** Known error patterns mapped to user-friendly messages */
const ERROR_MAP: Array<{ pattern: RegExp; message: string }> = [
  { pattern: /user rejected/i, message: 'Transaction was rejected' },
  { pattern: /user denied/i, message: 'Transaction was rejected' },
  { pattern: /insufficient funds/i, message: 'Insufficient funds for transaction' },
  { pattern: /nonce too low/i, message: 'Transaction conflict - please try again' },
  { pattern: /execution reverted/i, message: 'Transaction would fail - please check parameters' },
  { pattern: /gas required exceeds/i, message: 'Transaction requires too much gas' },
  { pattern: /network error/i, message: 'Network error - please check your connection' },
  { pattern: /timeout/i, message: 'Request timed out - please try again' },
  { pattern: /CALL_EXCEPTION/i, message: 'Contract call failed - please try again' },
  { pattern: /ACTION_REJECTED/i, message: 'Transaction was rejected' },
  { pattern: /UNPREDICTABLE_GAS_LIMIT/i, message: 'Unable to estimate gas - transaction may fail' },
];

/**
 * The message an error carries: an Error's, a plain-object provider error's (EIP-1193 providers
 * reject with `{ code, message }`), or one nested one level down. Null when there is none.
 */
function readMessage(error: unknown): string | null {
  if (error instanceof Error) return error.message;
  if (typeof error !== 'object' || error === null) return null;
  const record = error as Record<string, unknown>;
  if (typeof record.message === 'string' && record.message.length > 0) return record.message;
  for (const key of ['error', 'data', 'cause']) {
    const inner = record[key];
    if (typeof inner === 'object' && inner !== null) {
      const message = (inner as Record<string, unknown>).message;
      if (typeof message === 'string' && message.length > 0) return message;
    }
  }
  return null;
}

/**
 * Sanitize an error for user display.
 * Returns a user-friendly message, stripping any sensitive data.
 */
export function sanitizeErrorMessage(error: unknown, fallback: string): string {
  const raw = readMessage(error);
  if (raw === null) return fallback;

  // Check for known error patterns first
  for (const { pattern, message } of ERROR_MAP) {
    if (pattern.test(raw)) return message;
  }

  // If the message contains sensitive patterns, use the fallback
  for (const pattern of SENSITIVE_PATTERNS) {
    if (pattern.test(raw)) return fallback;
  }

  // If the message is short and clean, it's likely safe to show
  if (raw.length <= 120 && !raw.includes('\n') && !raw.includes('at ')) {
    return raw;
  }

  // Long or multi-line messages likely contain stack traces or debug info
  return fallback;
}
